/**
 * PRF (Pseudo-Random Function) Encryption Module
 *
 * Uses WebAuthn PRF extension to derive AES encryption keys from passkeys.
 * This allows "one-click" biometric authentication to unlock the session signer
 * without requiring a PIN.
 *
 * Browser Support:
 * - Chrome 116+ (Windows Hello, Touch ID)
 * - Safari 17+ (Touch ID, Face ID via macOS)
 * - Edge 116+ (Windows Hello)
 * - Firefox: NOT supported (falls back to PIN)
 */

// Salt for PRF derivation - must be consistent across sessions
const PRF_SALT = new TextEncoder().encode('lumenbro-session-signer-v1');

/**
 * Check if the browser supports WebAuthn PRF extension.
 * This is required for passkey-based encryption.
 */
export async function supportsPRF(): Promise<boolean> {
  try {
    // Check for PublicKeyCredential API
    if (typeof PublicKeyCredential === 'undefined') {
      return false;
    }

    // Check for getClientCapabilities (newer API)
    // @ts-ignore - Not in all TypeScript definitions yet
    if (PublicKeyCredential.getClientCapabilities) {
      // @ts-ignore
      const capabilities = await PublicKeyCredential.getClientCapabilities();
      const extensions = capabilities?.extensions;
      if (Array.isArray(extensions) && extensions.includes('prf')) {
        return true;
      }
    }

    // Fallback: Try to detect via feature detection
    // If we can call get() with PRF extension and it doesn't throw, it's supported
    // This is a heuristic - actual support determined at credential usage time
    return typeof navigator.credentials?.get === 'function';
  } catch {
    return false;
  }
}

/**
 * Derive an AES-GCM-256 encryption key from a passkey using PRF extension.
 *
 * This triggers biometric authentication (Face ID, Touch ID, Windows Hello)
 * and uses the PRF output as key material.
 *
 * @param credentialId - The credential ID of the passkey to use
 * @returns AES-GCM-256 CryptoKey for encryption/decryption
 * @throws If PRF is not supported or biometric fails
 */
export async function deriveKeyWithPRF(credentialId: Uint8Array): Promise<CryptoKey> {
  // Request credential with PRF extension
  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      allowCredentials: [
        {
          id: credentialId,
          type: 'public-key',
          // Allow all transports - let the browser figure it out
          transports: ['internal', 'hybrid', 'usb', 'ble', 'nfc'],
        },
      ],
      userVerification: 'required', // Require biometric
      extensions: {
        // @ts-ignore - PRF extension not in all TypeScript definitions
        prf: {
          eval: { first: PRF_SALT },
        },
      },
    },
  })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error('Credential request cancelled');
  }

  // Extract PRF output
  // @ts-ignore - PRF extension results not in all TypeScript definitions
  const prfOutput = credential.getClientExtensionResults()?.prf?.results?.first;

  if (!prfOutput || !(prfOutput instanceof ArrayBuffer)) {
    throw new Error('PRF extension not supported or failed to produce output');
  }

  // Import PRF output as AES-GCM key
  return deriveKeyFromPRFOutput(prfOutput);
}

/**
 * Derive an AES-GCM-256 encryption key from raw PRF output.
 *
 * This is used when the PRF output has already been obtained during
 * authentication (e.g., from the main app's login flow), allowing
 * single-biometric login+unlock without a second WebAuthn ceremony.
 *
 * @param prfOutput - Raw PRF output from WebAuthn (ArrayBuffer or Uint8Array)
 * @returns AES-GCM-256 CryptoKey for encryption/decryption
 */
export async function deriveKeyFromPRFOutput(
  prfOutput: ArrayBuffer | Uint8Array
): Promise<CryptoKey> {
  // Ensure we have an ArrayBuffer
  const keyMaterial =
    prfOutput instanceof ArrayBuffer ? prfOutput : prfOutput.buffer.slice(
      prfOutput.byteOffset,
      prfOutput.byteOffset + prfOutput.byteLength
    );

  // Import PRF output as AES-GCM key
  return crypto.subtle.importKey('raw', keyMaterial, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
}

/**
 * Get the credential ID for PRF operations.
 * Returns the first available passkey credential ID.
 *
 * @param rpId - Relying Party ID (domain)
 * @returns Credential ID as Uint8Array, or null if none found
 */
export async function getCredentialIdForPRF(rpId: string): Promise<Uint8Array | null> {
  // For security, we can't enumerate credentials directly
  // The credential ID must be stored after registration
  // This is a placeholder - actual implementation needs credential storage
  console.warn('[PRF] getCredentialIdForPRF requires stored credential ID');
  return null;
}
